import { db, auth } from '../firebase';
import { 
  doc, 
  collection, 
  addDoc,
  updateDoc,
  Timestamp,
  query,
  where,
  getDocs,
  orderBy
} from 'firebase/firestore';

// Interface for session data
export interface UserSession {
  userId: string;
  endTime: Timestamp;
  duration: number; // in seconds
}

// Track the current focus timer value
let currentFocusTimerValue: number = 0;

// Track the current session ID
let currentSessionId: string | null = null;

// Track the update interval
let sessionUpdateInterval: NodeJS.Timeout | null = null;

// Flag to track if we've already set up the auth listener
let authListenerInitialized = false;

// Timestamp of when we last created a session (to prevent duplicates)
let lastSessionCreationTime = 0;

/**
 * Format seconds into a human-readable duration string
 * @param seconds Total seconds
 * @returns Formatted string like "1h 23m 45s"
 */
const formatDuration = (seconds: number): string => {
  if (seconds < 60) {
    return `${seconds}s`;
  }
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;
  
  let result = '';
  if (hours > 0) {
    result += `${hours}h `;
  }
  if (minutes > 0 || hours > 0) {
    result += `${minutes}m `;
  }
  result += `${remainingSeconds}s`;
  
  return result.trim();
};

/**
 * Format seconds as HH:MM:SS
 * @param seconds Total seconds
 * @returns Formatted string like "01:23:45"
 */
export const formatTimeHHMMSS = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Update the current focus timer value
 * @param value The current focus timer value in seconds
 */
export const updateFocusTimerValue = (value: number): void => {
  currentFocusTimerValue = value;
};

/**
 * Create a new session in Firebase when the app starts
 */
const createSession = async (): Promise<string | null> => {
  try {
    const currentUser = auth.currentUser;
    
    // Only create a session if there's an authenticated user
    if (!currentUser) {
      return null;
    }
    
    // Prevent creating a new session if one was created very recently (within 3 seconds)
    const now = Date.now();
    if (now - lastSessionCreationTime < 3000) {
      console.log('Session creation throttled - too soon since last creation');
      return currentSessionId; // Return the existing session ID
    }
    
    // Update the session creation timestamp
    lastSessionCreationTime = now;
    
    // Create a new session object
    const sessionData: UserSession = {
      userId: currentUser.uid,
      endTime: Timestamp.fromMillis(now),
      duration: 0 // Start with 0 duration
    };
    
    // Create a reference to the user's sessions subcollection
    const userSessionsCollection = collection(db, `users/${currentUser.uid}/sessions`);
    
    // Add the new session to Firebase
    const sessionRef = await addDoc(userSessionsCollection, sessionData);
    const newSessionId = sessionRef.id;
    
    console.log('Created new session in Firebase:', newSessionId);
    
    // Return the session ID
    return newSessionId;
    
  } catch (error) {
    console.error('Error creating session in Firebase:', error);
    return null;
  }
};

/**
 * Update the current session in Firebase
 */
export const updateSession = async (): Promise<void> => {
  try {
    const currentUser = auth.currentUser;
    
    // Only update the session if there's an authenticated user and a current session
    if (!currentUser || !currentSessionId) {
      return;
    }
    
    // Create a reference to the current session document
    const sessionRef = doc(db, `users/${currentUser.uid}/sessions/${currentSessionId}`);
    
    // Update the session with the current timer value and end time
    await updateDoc(sessionRef, {
      endTime: Timestamp.fromMillis(Date.now()),
      duration: currentFocusTimerValue
    });
    
    console.log('Updated session in Firebase:', currentSessionId, 'Duration:', formatDuration(currentFocusTimerValue));
    
  } catch (error) {
    console.error('Error updating session in Firebase:', error);
  }
};

/**
 * Get the total time spent across all sessions
 * @returns Promise with total time in seconds
 */
export const getTotalSessionTime = async (): Promise<number> => {
  try {
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      return 0;
    }
    
    // Create a reference to the user's sessions subcollection
    const userSessionsCollection = collection(db, `users/${currentUser.uid}/sessions`);
    
    // Query all sessions for this user
    const sessionsQuery = query(
      userSessionsCollection,
      orderBy('endTime', 'desc')
    );
    
    const querySnapshot = await getDocs(sessionsQuery);
    
    let totalDuration = 0;
    
    // Sum up all session durations
    querySnapshot.forEach((doc) => {
      const sessionData = doc.data() as UserSession;
      totalDuration += sessionData.duration;
    });
    
    return totalDuration;
  } catch (error) {
    console.error('Error getting total session time:', error);
    return 0;
  }
};

/**
 * Setup session tracking - creates a new session and updates it regularly
 */
export const setupSessionTracking = async (): Promise<void> => {
  // If we've already initialized the auth listener, just return
  if (authListenerInitialized) {
    console.log('Session tracking already initialized, skipping duplicate setup');
    return;
  }
  
  // Mark that we've initialized the auth listener
  authListenerInitialized = true;
  
  // Clear any existing interval
  if (sessionUpdateInterval) {
    clearInterval(sessionUpdateInterval);
    sessionUpdateInterval = null;
  }
  
  // Check if a user is already logged in, if so create a session immediately
  if (auth.currentUser) {
    currentSessionId = await createSession();
    
    if (currentSessionId) {
      sessionUpdateInterval = setInterval(updateSession, 60000); // 60000ms = 1 minute
      updateSession(); // Update immediately
    }
  }
  
  // Setup auth state change listener
  auth.onAuthStateChanged(async (user) => {
    // Clear any existing interval first
    if (sessionUpdateInterval) {
      clearInterval(sessionUpdateInterval);
      sessionUpdateInterval = null;
    }
    
    if (user) {
      // Only create a new session if we don't already have one
      if (!currentSessionId) {
        currentSessionId = await createSession();
        
        if (currentSessionId) {
          sessionUpdateInterval = setInterval(updateSession, 60000); // 60000ms = 1 minute
          updateSession(); // Update immediately
        }
      } else {
        // If we already have a session ID, just set up the interval again
        sessionUpdateInterval = setInterval(updateSession, 60000); // 60000ms = 1 minute
        updateSession(); // Update immediately
      }
    } else {
      // User logged out, clear the session tracking
      currentSessionId = null;
    }
  });
  
  // Update the session before the user leaves the page
  const handleBeforeUnload = () => {
    if (currentSessionId && auth.currentUser) {
      try {
        // Try to update one last time before page unloads
        // This is best-effort and may not always succeed
        updateSession();

        // Since we update every minute, the data loss should be minimal
        // even if this final update doesn't complete
      } catch (error) {
        console.error('Error in beforeunload handler:', error);
      }
    }
  };
  
  window.addEventListener('beforeunload', handleBeforeUnload);
};

/**
 * Get the daily focus time data for the last 7 days
 * @returns Promise with an array of {day, value} objects for charting
 */
export const getDailyFocusTimeData = async (days = 7): Promise<{ day: string; value: number }[]> => {
  try {
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      return [];
    }
    
    // Calculate the date range (n days ago until today)
    const endDate = new Date();
    endDate.setHours(23, 59, 59, 999); // End of today
    
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - (days - 1)); // Start from 7 days ago
    startDate.setHours(0, 0, 0, 0); // Start of the day
    
    // Create a reference to the user's sessions subcollection
    const userSessionsCollection = collection(db, `users/${currentUser.uid}/sessions`);
    
    // Query sessions within the date range
    const sessionsQuery = query(
      userSessionsCollection,
      where('endTime', '>=', Timestamp.fromDate(startDate)),
      where('endTime', '<=', Timestamp.fromDate(endDate)),
      orderBy('endTime', 'asc')
    );
    
    const querySnapshot = await getDocs(sessionsQuery);
    
    // Create a map to store total duration for each day
    const dailyTotals: { [key: string]: number } = {};
    
    // Initialize all days in the range with 0 values
    for (let i = 0; i < days; i++) {
      const date = new Date(startDate);
      date.setDate(date.getDate() + i);
      const dayKey = formatDateKey(date);
      dailyTotals[dayKey] = 0;
    }
    
    // Sum up session durations for each day
    querySnapshot.forEach((doc) => {
      const sessionData = doc.data() as UserSession;
      const sessionDate = sessionData.endTime.toDate();
      const dayKey = formatDateKey(sessionDate);
      
      // Only add if the key exists (within our date range)
      if (dailyTotals.hasOwnProperty(dayKey)) {
        dailyTotals[dayKey] += sessionData.duration;
      }
    });
    
    // Convert to the format required for charting
    const result = Object.entries(dailyTotals).map(([day, value]) => ({
      day: formatDayLabel(day),
      value
    }));
    
    return result;
  } catch (error) {
    console.error('Error getting daily focus time data:', error);
    return [];
  }
};

/**
 * Format a date as YYYY-MM-DD for consistent keys
 */
const formatDateKey = (date: Date): string => {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

/**
 * Format a day label for the chart (e.g., "Mon" for Monday)
 */
const formatDayLabel = (dateKey: string): string => {
  // Convert YYYY-MM-DD to a Date object
  const [year, month, day] = dateKey.split('-').map(num => parseInt(num, 10));
  const date = new Date(year, month - 1, day);
  
  // Get day of week as a string (e.g., "Mon")
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return days[date.getDay()];
}; 